import { Sparkles, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Toggle that lives in a widget header and reveals that widget's insights.
 * Labelled as AI so a generated reading of the data is never mistaken for a
 * computed figure.
 */
export function AiInsightsButton({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'secondary' : 'ghost'}
      aria-pressed={active}
      onClick={onClick}
      className="h-7 gap-1 px-2 text-xs"
    >
      <Sparkles className={cn('size-3.5', active && 'text-primary')} aria-hidden />
      Insights
    </Button>
  );
}

/**
 * Panel of generated commentary for one widget. Docks to the right of the
 * widget's content (the widget shrinks to make room) rather than covering it,
 * so the reader can check a claim against the chart it describes.
 *
 * `bullets` are supplied by the caller today. When the dashboard APIs land,
 * this is where a generated-summary query renders.
 */
export function AiInsightsPanel({
  title = 'Insights',
  bullets,
  footnote,
  onClose,
}: {
  title?: string;
  bullets: ReactNode[];
  /** What the commentary was derived from — keeps its provenance visible. */
  footnote?: string;
  onClose: () => void;
}) {
  return (
    <aside className="flex h-full min-h-0 w-[290px] shrink-0 flex-col gap-2 overflow-hidden rounded-lg border bg-muted/40 p-3">
      <div className="flex items-center gap-2">
        <Sparkles className="text-primary" aria-hidden />
        <h4 className="flex-1 font-medium text-sm">{title}</h4>
        <Badge variant="outline" className="text-[10px]">
          AI-generated
        </Badge>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-6"
          onClick={onClose}
          aria-label="Close insights"
        >
          <X className="size-3.5" aria-hidden />
        </Button>
      </div>
      <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto text-muted-foreground text-xs leading-relaxed">
        {/* Index keys: the bullets are a fixed, ordered block of prose with no
            identity of their own — they are never reordered or filtered. */}
        {bullets.map((b, i) => (
          <li key={i} className="border-border/60 border-l-2 pl-2">
            {b}
          </li>
        ))}
      </ul>
      {footnote ? <p className="text-[10px] text-muted-foreground/80">{footnote}</p> : null}
    </aside>
  );
}
